import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useReportContentGateReached } from './content-gate-reached';
import { useSetAtom } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { BLEED_BREAKOUT_X } from '~/ui-rocco/pages/detail-shared/large-screen';
import { useDirectoryBrowseTheme } from '~/ui-rocco/pages/directory-browse/directory-browse-theme';
import { Grid } from '@rocco/ui/layout/components/Grid';
import type { ContentGateBehindTile } from './content-gate';
import { useImpression, useTracker } from '@archipro-website/tracker';
import { useSetLoginModal } from '~/modules-rocco/user/hook/use-open-login-modal';
import { contentGateElementAtom } from '~/modules-rocco/root/states/ui-state-content-gate-in-view';

/**
 * The chevron from the design (Figma 4708:48054). A filled path, not a stroked
 * icon — stroked at this size it renders as a hairline and reads both thinner
 * and smaller than the design, which is what a lucide ChevronRight was doing
 * here.
 *
 * The outer span is the icon's own frame, wider than the glyph it holds.
 * Dropping it and spacing to the glyph directly closes the gap by the frame's
 * padding, and the button stops matching.
 */
const GateChevron = () => (
    <span className="flex size-[0.7373rem] shrink-0 items-center justify-center">
        <svg
            viewBox="0 0 6.74555 11.7969"
            fill="currentColor"
            aria-hidden="true"
            className="h-full w-auto"
        >
            <path d="M0.621067 0.0936456L0.0936256 0.621061C-0.0312085 0.745895 -0.0312085 0.948304 0.0936256 1.07316L4.90741 5.89843L0.0936256 10.7237C-0.0312085 10.8485 -0.0312085 11.0509 0.0936256 11.1758L0.621067 11.7032C0.745901 11.8281 0.948311 11.8281 1.07317 11.7032L6.65192 6.1245C6.77676 5.99967 6.77676 5.79726 6.65192 5.6724L1.07317 0.0936456C0.948311 -0.0312152 0.745901 -0.0312152 0.621067 0.0936456V0.0936456Z" />
        </svg>
    </span>
);

interface ContentGatePanelProps {
    /** Results in the full catalogue, for the share-seen line. */
    totalCount: number;
    /** Tiles the visitor was allowed, for the share-seen line. */
    visibleTiles: number;
    /** Plural noun for the unlock line ("products", "projects", "professionals"). */
    entityNoun: string;
    /**
     * Catalogue imagery that scrolls behind the pinned copy. Empty falls back
     * to the static panel — there is nothing to scroll past.
     */
    behindTiles?: ContentGateBehindTile[];
    /**
     * The grid's own column, gap and tile-aspect classes. Passed rather than
     * rebuilt: the run has to line up with the rows above it exactly, and the
     * gaps in particular are easy to approximate and get subtly wrong.
     */
    colClasses?: string;
    gapClasses?: string;
    tileAspectClass?: string;
}

/**
 * The sign-in wall that closes a gated category grid: the tail of the grid
 * blurs out under a gradient, and the visitor is asked to create an account to
 * keep going.
 *
 * Geometry follows Figma 4708:48049. The headline is deliberately held to a
 * narrow measure so it breaks to two lines — full-bleed it reads as a banner
 * rather than a statement, and the break is part of the composition. The
 * copy block, the measure and the single pill are all centred on the same axis.
 *
 * Rendered in flow but pulled up over the preceding rows by a negative margin,
 * so it works the same in the revamped uniform grid and the legacy sectioned one
 * without either having to hand it a positioning context. The blur is a
 * backdrop-filter with a mask, so it fades in over the tiles behind it rather
 * than cutting a hard line across the grid.
 *
 * The grid above is already truncated server-side — this only hides the last
 * row it was given. Nothing further down the catalogue is in the page to reveal.
 */
/**
 * How far through the run the scrim reaches full strength. Early: the page
 * colour should be closing over the grid well before the run ends, so the last
 * stretch reads as the catalogue already gone rather than still going.
 */
const SCRIM_FULL_AT = 0.4;

/**
 * Scroll progress through the gated run, 0 while its top is still below the
 * viewport and 1 once it has been scrolled clear.
 */
const runProgress = (rect: DOMRect, viewportHeight: number): number => {
    // Measured from the run filling the viewport, not from it first appearing:
    // the copy pins on a light wash and the darkening is what happens as you
    // push on through, so the ramp must not already be underway when it lands.
    const travelled = -rect.top;
    const distance = Math.max(1, rect.height - viewportHeight);
    return Math.min(1, Math.max(0, travelled / distance));
};

/**
 * Where the frost takes the last real row, as a share of the tile measured up
 * from its bottom edge. Enough that the grid stops on a soft edge rather than a
 * cut one, while leaving the top of the row readable as products.
 */
const ROW_FROST_SHARE = 0.6;

/**
 * How far the reader scrolls between the last row fading out and the copy
 * rising to meet them. Viewport-relative because it is a scroll rhythm rather
 * than a size — the reader should get the same beat on any screen.
 */
const GATE_COPY_LEAD_IN = 'h-[55vh]';
const GATE_COPY_LEAD_IN_PULL = '-mt-[55vh]';

export const ContentGatePanel = ({
    totalCount,
    visibleTiles,
    entityNoun,
    behindTiles,
    colClasses,
    gapClasses,
    tileAspectClass = 'aspect-tile-portrait',
}: ContentGatePanelProps) => {
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const tracker = useTracker();
    const { setLoginModal } = useSetLoginModal();
    const panelRef = useRef<HTMLDivElement>(null);
    const setGateElement = useSetAtom(contentGateElementAtom);

    // Register where the blur starts, so the floating surfaces can bound
    // themselves against it. Cleared on unmount: navigating to a category
    // without a gate must not leave them bounded by an element that is gone.
    useEffect(() => {
        setGateElement(panelRef.current);
        return () => setGateElement(null);
    }, [setGateElement]);

    useImpression({
        ref: panelRef,
        onImpression: async (url: URL) => {
            // Source and ExtraData are the only custom keys the warehouse
            // pipeline persists; the flat keys serve network QA.
            tracker.log('ContentGateView', {
                url,
                data: {
                    authSource: 'contentGate',
                    visibleTiles,
                    totalCount,
                    Source: 'contentGate',
                    ExtraData: JSON.stringify({
                        authSource: 'contentGate',
                        visibleTiles,
                        totalCount,
                    }),
                },
            });
        },
    });

    // Scroll progress drives the scrim, so the run darkens as it goes by rather
    // than arriving fully dark. Read on rAF from a rect only — no layout is
    // written during scroll — and skipped entirely when the run is absent.
    const runRef = useRef<HTMLDivElement>(null);
    // How far the frost reaches back up out of the run, in pixels.
    //
    // Measured against the last real tile rather than derived from the run's
    // own: the run begins below the captions and the row gap, so anything
    // counted from its top lands in the gap and never touches the image. What
    // the design asks for is a position inside that image — 60% down it — so
    // the row is cut mid-picture and its bottom fades into the field below.
    const [reachBack, setReachBack] = useState(0);
    // Tells the page chrome the grid has ended here, so the filter bar above
    // can let go rather than float over a wall it cannot filter past.
    useReportContentGateReached(runRef);
    const hasRun = !!behindTiles?.length;

    useLayoutEffect(() => {
        const run = runRef.current;
        if (!hasRun || !run || typeof ResizeObserver === 'undefined') return;

        const measure = () => {
            // The grid is the panel's previous sibling; its last tile image is
            // the row being cut. Anything unexpected in that shape leaves the
            // reach at 0, which is the pre-design behaviour rather than a
            // broken one.
            const grid = run.parentElement?.previousElementSibling;
            const images = grid?.querySelectorAll('img');
            const last = images?.[images.length - 1];
            if (!last) return setReachBack(0);

            const imageRect = last.getBoundingClientRect();
            if (!imageRect.height) return setReachBack(0);

            const cutAt = imageRect.bottom - imageRect.height * ROW_FROST_SHARE;
            // A fixed point, because the value being measured is also what
            // moves the run: fold the gap that is left into the offset already
            // applied and it settles in one pass. The 1px deadband is what
            // stops it oscillating on sub-pixel rounding.
            setReachBack((applied) => {
                const next = Math.max(
                    0,
                    Math.round(
                        applied + (run.getBoundingClientRect().top - cutAt),
                    ),
                );
                return Math.abs(next - applied) > 1 ? next : applied;
            });
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(run);
        if (run.parentElement) observer.observe(run.parentElement);
        return () => observer.disconnect();
    }, [hasRun, behindTiles]);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (!hasRun) return;
        const reduced = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        if (reduced) {
            // No ramp to follow: settle on the end state so the copy still has
            // the contrast it was drawn for. The gate is still on the page, so
            // the surfaces that stand down for it still need telling.
            setProgress(1);
            return;
        }

        let frame = 0;
        const read = () => {
            frame = 0;
            const node = runRef.current;
            if (!node) return;
            const rect = node.getBoundingClientRect();
            setProgress(runProgress(rect, window.innerHeight));
        };
        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(read);
        };

        read();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            if (frame) cancelAnimationFrame(frame);
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [hasRun]);

    const handleCta = () => {
        tracker.log('LoginPromptCtaClick', {
            url: new URL(window.location.href),
            data: {
                authSource: 'contentGate',
                flow: 'signup',
                Source: 'contentGate',
                ExtraData: JSON.stringify({
                    authSource: 'contentGate',
                    flow: 'signup',
                }),
            },
        });
        setLoginModal({ authSource: 'contentGate', isOpen: true });
    };

    // The trailing margin is desktop-only: on a wide viewport the unmasked
    // section beneath sits far closer to the CTA than the padding alone allows
    // for, and the gate needs clear air under it to read as an ending.
    // Shared by both shapes: the copy is identical, only its backdrop differs.
    // Past this the scrim carries the copy, so the ink flips to the palette the
    // design draws for it. The prototype does the same: dark type on the
    // opening wash, white once the run has gone dark. Transitioned rather than
    // switched so it reads as one move.
    // The scrim resolves toward the page's own colour, so the copy never has to
    // change hands mid-scroll: dark ink on a light vertical, light on a dark one.
    // The light verticals' glass resolves toward page white, so their copy
    // stays dark ink the whole way through — white type would be legible for a
    // moment in the middle and gone by the end. White is the dark verticals'.
    const onScrim = isDark;

    const copy = (
        <div className="relative flex flex-col items-center px-4 text-center">
            {/* Held to the design's measure so the line breaks where the
                    composition wants it, not where the viewport ends. */}
            <h2
                className={cn(
                    'm-0 max-w-[31.0625rem] text-[1.75rem] leading-[1.16] font-normal transition-colors duration-500 md:text-[2.6875rem]',
                    onScrim
                        ? 'text-white [text-shadow:0_1px_28px_rgba(0,0,0,0.45)]'
                        : 'text-[#141414] [text-shadow:0_1px_28px_rgba(255,255,255,0.7)]',
                )}
            >
                You&rsquo;ve seen less than 1% of what&rsquo;s available.
            </h2>
            <p
                className={cn(
                    'm-0 mt-2.5 max-w-[33.375rem] text-sm leading-[1.16] transition-colors duration-500 md:text-[1.375rem]',
                    onScrim
                        ? 'text-[#c7c3c0] [text-shadow:0_1px_20px_rgba(0,0,0,0.5)]'
                        : 'text-[#767676] [text-shadow:0_1px_20px_rgba(255,255,255,0.8)]',
                )}
            >
                Unlock all {totalCount.toLocaleString()} {entityNoun}, plus
                saved boards and direct enquiries. New listings added every day.
            </p>
            <button
                type="button"
                onClick={handleCta}
                className={cn(
                    'mt-6 flex h-[3.25rem] w-[14.25rem] items-center justify-center gap-3 rounded-[var(--radius-btn-l)] text-base font-medium tracking-[0.02em] transition-colors duration-500',
                    onScrim
                        ? 'bg-[#f3f3f3] text-[#181818] hover:bg-white'
                        : 'bg-[#181818] text-[#f3f3f3] hover:bg-[#323232]',
                )}
            >
                Create a Free account
                <GateChevron />
            </button>
        </div>
    );

    // No run to scroll past — the static panel that closes the grid.
    if (!hasRun) {
        return (
            <div
                ref={panelRef}
                className="relative -mt-80 pt-80 pb-24 md:-mt-125 md:mb-40 md:pt-125 md:pb-40"
            >
                <div
                    aria-hidden="true"
                    className="absolute -inset-x-4 inset-y-0 [mask-image:linear-gradient(to_bottom,transparent_0%,black_45%)] backdrop-blur-[1.0625rem] md:-inset-x-12"
                />
                <div
                    aria-hidden="true"
                    className={cn(
                        'absolute -inset-x-4 inset-y-0 md:-inset-x-12',
                        isDark
                            ? 'bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_2%,rgba(0,0,0,0.28)_56%,rgba(0,0,0,0.42)_80%,rgba(0,0,0,0)_100%)]'
                            : 'bg-[linear-gradient(to_bottom,transparent_0%,transparent_42%,rgba(255,255,255,0.82)_72%,#fff_88%)]',
                    )}
                />
                {copy}
            </div>
        );
    }

    // The gated run: real catalogue imagery scrolling past, the scrim
    // deepening as it goes, and the copy pinned to the middle of the viewport
    // throughout. The run ends, the copy unpins, and the page carries on into
    // whatever section follows.
    const scrim = Math.min(1, progress / SCRIM_FULL_AT) * 0.92;
    const fade = `linear-gradient(to bottom, transparent 0px, black ${reachBack}px, black 86%, transparent 100%)`;

    return (
        <div ref={panelRef}>
            {/* Continues the grid rather than overlapping it: pulled up over
                the rows above, the run's tiles landed on top of their captions
                and the join read as a rendering fault. The row gap is the same
                one the grid uses between its own rows, so the run arrives as
                the next rows would. */}
            <div
                ref={runRef}
                className={cn(
                    'relative mt-12 md:mt-[6.25rem]',
                    // The field is the page's, not the column's. Left inside
                    // the container it stopped at the content edge and drew a
                    // vertical seam down both sides on wide screens.
                    '-mx-4',
                    BLEED_BREAKOUT_X,
                )}
                // Pulled up onto the last row so the frost, the wash and the
                // blurred tiles are one surface with one mask. Held apart as
                // three stacked boxes they each ended on their own edge, and
                // the topmost one drew a hard line across the field where its
                // mask ran out and the next one's began.
                style={{ marginTop: reachBack ? -reachBack : undefined }}
            >
                {/* A zero-height sticky rail, placed before the run so it pins
                    from the moment the run's top passes the middle of the
                    screen. After the grid its normal position would be the far
                    end, and it would only appear once everything had already
                    scrolled by. */}
                {/* Lead-in: without it the copy pins the instant the run's top
                    reaches the middle of the screen, arriving hard against the
                    boundary with no glass above it. The design gives it a good
                    band to sit below. */}
                {/* The gap the copy arrives after: the reader watches the last
                    row fade, scrolls through this, and only then does the text
                    come up to meet them.

                    A real element rather than a margin on the rail below. The
                    rail is zero-height, so its own top margin collapses through
                    the run's top edge and moves the run instead of the rail —
                    which drags the whole field up over the grid and leaves the
                    copy pinned across the last row of real tiles. A box with
                    height cannot collapse. */}
                <div aria-hidden="true" className={GATE_COPY_LEAD_IN} />
                <div className="sticky top-[50vh] z-10 h-0">
                    <div className="-translate-y-1/2">{copy}</div>
                </div>
                {/* Imagery only: no link, no title, no tracking, and blurred
                    with `filter` on the images themselves rather than a
                    backdrop pass over the top — a per-frame backdrop blur
                    across a pinned, scrolling viewport is the expensive one. */}
                {/* No padding of its own: the panel already sits inside the
                    grid's gutters, so adding them again pushes the run out of
                    step with the rows above it. */}
                {/* Blurred as one surface rather than tile by tile: blurring
                    each image inside its own box leaves every tile a hard
                    rectangular edge with the gutters still white, which reads
                    as blurred cards laid on a page. Taken across the whole
                    field the tiles and the gaps melt into each other and the
                    bounds disappear, which is what the prototype does. Scaled
                    up slightly so the blur's soft edge falls outside the frame
                    rather than fading the outermost tiles. */}
                {/* The field is bounded by the grid's gutters, so without this
                    its blurred content stops dead against the page down both
                    sides. Fading the flanks costs only the outermost tiles'
                    edges, which carry nothing. Kept on its own element: the top
                    fade lives on the child, and composing two masks on one box
                    is far less portable than nesting them. */}
                <div
                    className={cn(
                        '[mask-image:linear-gradient(to_right,transparent_0%,black_5%,black_95%,transparent_100%)]',
                        // Cancels the lead-in above. The gap is for the copy's
                        // timing only; the field still has to start where the
                        // row was cut, or the mask ramp — measured in pixels
                        // from the run's top — lands somewhere else entirely.
                        GATE_COPY_LEAD_IN_PULL,
                    )}
                >
                    <div
                        className={cn(
                            'blur-2xl',
                            // Frosted, not merely blurred. Raw blurred product
                            // photography keeps all its contrast and saturation, so
                            // a dark tile smears into a black bruise and a warm one
                            // into a stain. Pulling the saturation down and the
                            // brightness up turns the field into glass.
                            // Lifted on both, the dark verticals included:
                            // their photography is already dark, and dimming it
                            // further to "frost" it only crushes it to black.
                            isDark
                                ? 'brightness-[1.15] saturate-[0.5]'
                                : 'brightness-[1.04] saturate-[0.4]',
                        )}
                        // In pixels, not percent: the ramp has to finish
                        // exactly where the row was cut, and a percentage of
                        // the run's own height is a different distance on every
                        // page.
                        style={{ maskImage: fade, WebkitMaskImage: fade }}
                    >
                        <Grid colClasses={colClasses} rowsGapClass={gapClasses}>
                            {behindTiles?.map((tile) => (
                                <div
                                    key={tile.id}
                                    className={cn(
                                        'w-full overflow-hidden rounded-tile',
                                        tileAspectClass,
                                    )}
                                >
                                    <img
                                        src={tile.src}
                                        alt=""
                                        // Thumbnails, a couple of KB each. Lazy
                                        // loading buys nothing and an undecoded one
                                        // punches a hard-edged hole in a field
                                        // whose whole point is that it has no edges.
                                        decoding="async"
                                        className="size-full object-cover"
                                    />
                                </div>
                            ))}
                        </Grid>
                    </div>
                </div>
                {/* The milk in the glass: present from the first frame, before
                    the scroll-linked scrim has done anything, so the field
                    reads as frosted rather than as a photograph out of focus.
                    Masked like the blur so it does not draw its own edge. */}
                <div
                    aria-hidden="true"
                    className={cn(
                        'pointer-events-none absolute inset-0',
                        // Carries the blur as well as the wash. One element
                        // over the whole field means the real tiles showing
                        // through the top of the ramp are frosted by the same
                        // pass that milks the blurred ones below — which is
                        // what removes the seam rather than hiding it.
                        'backdrop-blur-[1.0625rem]',
                        isDark ? 'bg-black/35' : 'bg-white/28',
                    )}
                    style={{ maskImage: fade, WebkitMaskImage: fade }}
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0"
                    style={{
                        // Toward the page's own colour, so the run dissolves
                        // into the section beneath it instead of ending in a
                        // band the rest of the page has to recover from. Black
                        // belongs to the dark verticals only.
                        // The page's own colour on both, not black on one and
                        // white on the other: resolving a dark vertical to pure
                        // black lands it below #141414, so the run reads as a
                        // darker rectangle sitting on the page with its own
                        // edges, which is exactly what it must not do.
                        backgroundColor: isDark
                            ? `rgba(20,20,20,${scrim})`
                            : `rgba(255,255,255,${scrim})`,
                    }}
                />
            </div>
            {/* The run resolves back to the page so the section beneath it
                arrives on page colour rather than against the scrim. */}
            <div
                aria-hidden="true"
                className={cn(
                    'h-40 md:h-64',
                    isDark
                        ? 'bg-linear-to-b from-[#141414] to-[#141414]'
                        : // From the page's own colour, not from a transparent
                          // near-black: interpolating #141414/0 toward white
                          // passes through grey, which draws a dirty band under
                          // the run exactly where it should be dissolving.
                          'bg-linear-to-b from-white/0 to-white',
                )}
            />
        </div>
    );
};
